import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for fetching field suggestions from existing data
 * Used for autocomplete/combobox functionality in forms
 * 
 * @param {string} entityName - Name of the entity (e.g., 'lead', 'customer')
 * @param {string} fieldName - Name of the field to get suggestions for
 * @param {object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable fetching (default: true)
 * @param {number} options.debounceMs - Debounce delay in ms (default: 300)
 * @param {number} options.limit - Max suggestions to return (default: 20)
 * @param {number} options.minChars - Min chars before searching (default: 0)
 * @returns {object} Hook state and methods
 */
export function useFieldSuggestions(entityName, fieldName, options = {}) {
  const {
    enabled = true,
    debounceMs = 300,
    limit = 20,
    minChars = 0
  } = options;

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // Cache for all suggestions (no search filter)
  const cacheRef = useRef({});
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  /**
   * Fetch suggestions from API
   */
  const fetchSuggestions = useCallback(async (search = '') => {
    if (!enabled || !entityName || !fieldName) {
      return;
    }

    // Check minChars requirement
    if (search && search.length < minChars) {
      setSuggestions([]);
      return;
    }

    // Check cache for non-filtered requests
    const cacheKey = `${entityName}:${fieldName}`;
    if (!search && cacheRef.current[cacheKey]) {
      const cached = cacheRef.current[cacheKey];
      setSuggestions(cached.suggestions);
      setTotal(cached.total);
      setHasMore(cached.hasMore);
      return;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantCode = localStorage.getItem('selectedTenantCode') || '';
      const selectedRole = localStorage.getItem('selectedRole');

      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', limit.toString());

      const url = `/api/field-suggestions/${encodeURIComponent(entityName)}/${encodeURIComponent(fieldName)}?${params}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch suggestions: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuggestions(data.suggestions || []);
        setTotal(data.total || 0);
        setHasMore(data.hasMore || false);

        // Cache non-filtered results
        if (!search) {
          cacheRef.current[cacheKey] = {
            suggestions: data.suggestions || [],
            total: data.total || 0,
            hasMore: data.hasMore || false,
            timestamp: Date.now()
          };
        }
      } else {
        throw new Error(data.error || 'Failed to fetch suggestions');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Error fetching field suggestions:', err);
      setError(err.message);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, entityName, fieldName, limit, minChars]);

  /**
   * Debounced search handler
   */
  const search = useCallback((term) => {
    setSearchTerm(term);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(term);
    }, debounceMs);
  }, [fetchSuggestions, debounceMs]);

  /**
   * Load all suggestions (no filter)
   */
  const loadAll = useCallback(() => {
    setSearchTerm('');
    fetchSuggestions('');
  }, [fetchSuggestions]);

  /**
   * Clear suggestions
   */
  const clear = useCallback(() => {
    setSuggestions([]);
    setSearchTerm('');
    setError(null);
  }, []);

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    cacheRef.current = {};
  }, []);

  // Initial load when enabled
  useEffect(() => {
    if (enabled && entityName && fieldName) {
      // Pre-load suggestions for better UX
      fetchSuggestions('');
    }
    
    return () => {
      // Cleanup
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enabled, entityName, fieldName]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    suggestions,
    loading,
    error,
    searchTerm,
    hasMore,
    total,
    search,
    loadAll,
    clear,
    clearCache
  };
}

/**
 * Filter suggestions client-side (useful when we have all data cached)
 * @param {Array} suggestions - Array of suggestion strings
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} Filtered suggestions
 */
export function filterSuggestions(suggestions, searchTerm) {
  if (!searchTerm || !searchTerm.trim()) {
    return suggestions;
  }
  
  const term = searchTerm.toLowerCase().trim();
  return suggestions.filter(s => 
    String(s).toLowerCase().includes(term)
  );
}

/**
 * Highlight matching text in suggestion
 * @param {string} text - The suggestion text
 * @param {string} highlight - The text to highlight
 * @returns {Array} Array of { text, isMatch } objects for rendering
 */
export function highlightMatch(text, highlight) {
  if (!highlight || !highlight.trim()) {
    return [{ text, isMatch: false }];
  }
  
  const textStr = String(text);
  const highlightLower = highlight.toLowerCase().trim();
  const textLower = textStr.toLowerCase();
  
  const index = textLower.indexOf(highlightLower);
  if (index === -1) {
    return [{ text: textStr, isMatch: false }];
  }
  
  const parts = [];
  if (index > 0) {
    parts.push({ text: textStr.substring(0, index), isMatch: false });
  }
  parts.push({ text: textStr.substring(index, index + highlight.length), isMatch: true });
  if (index + highlight.length < textStr.length) {
    parts.push({ text: textStr.substring(index + highlight.length), isMatch: false });
  }
  
  return parts;
}

export default useFieldSuggestions;
